import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ProfileEditForm } from '../components/profile/ProfileEditForm';
import { WebhookSettings } from '../components/profile/WebhookSettings';
import { ButtonSpinner } from '../components/ui/Loader';
import { Loader } from '../components/ui/Loader';
import { useGetUserProfileQuery } from '../store/api/userApi';
import { useToggle2faMutation, useConfirmEnable2faMutation } from '../store/api/authApi';

export function ProfilePage() {
  const { t } = useTranslation();
  const { data, isLoading, error, refetch } = useGetUserProfileQuery();
  const [isEditing, setIsEditing] = useState(false);
  const [toggle2fa, { isLoading: isToggling2fa }] = useToggle2faMutation();
  const [confirmEnable2fa, { isLoading: isConfirming2fa }] = useConfirmEnable2faMutation();

  // 2FA state
  const [show2faOtp, setShow2faOtp] = useState(false);
  const [otpAction, setOtpAction] = useState<'enable' | 'disable'>('enable');
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const [otpSuccess, setOtpSuccess] = useState<string | null>(null);
  const [otpEmail, setOtpEmail] = useState('');

  const handleEditSuccess = () => {
    setIsEditing(false);
    void refetch();
  };

  const handleToggle2fa = async (enable: boolean) => {
    setOtpError(null);
    setOtpSuccess(null);
    setOtp('');

    try {
      const result = await toggle2fa({ enable }).unwrap();
      
      if (result.data.action === 'otp_sent') {
        // Need OTP verification
        setOtpAction(enable ? 'enable' : 'disable');
        setShow2faOtp(true);
        setOtpEmail(result.data.email || '');
      } else {
        // Disabled successfully
        setOtpSuccess(enable ? t('profile.2fa.enabled') : t('profile.2fa.disabled'));
        void refetch();
      }
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      setOtpError(
        error?.data?.message || error?.message || t('errors.network')
      );
    }
  };

  const handleVerify2faOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError(null);
    setOtpSuccess(null);

    if (!otp.trim() || otp.trim().length < 4) {
      setOtpError(t('auth.login.otpInvalid'));
      return;
    }

    try {
      let result;
      if (otpAction === 'enable') {
        result = await confirmEnable2fa({ otp: otp.trim() }).unwrap();
      } else {
        result = await toggle2fa({ enable: false, otp: otp.trim() }).unwrap();
      }
      
      if (result.data.action === 'enabled') {
        setOtpSuccess(t('profile.2fa.enabled'));
        setShow2faOtp(false);
        void refetch();
      } else if (result.data.action === 'disabled') {
        setOtpSuccess(t('profile.2fa.disabled'));
        setShow2faOtp(false);
        void refetch();
      } else if (result.data.action === 'otp_sent') {
        setOtpError(t('profile.2fa.otpRequired'));
      }
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      setOtpError(
        error?.data?.message || error?.message || t('errors.network')
      );
    }
  };

  const handleToggle2faClick = () => {
    if (user.email2faEnabled) {
      handleToggle2fa(false);
    } else {
      handleToggle2fa(true);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-4xl mx-auto">
            <div className="flex justify-center items-center py-12">
              <Loader />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-4xl mx-auto">
            <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
              <h2 className="text-2xl font-heading font-bold text-danger mb-4">
                {t('profile.error.title')}
              </h2>
              <p className="text-neutral-medium font-body mb-6">
                {'data' in (error as { data?: { message?: string } }) 
                  ? ((error as { data?: { message?: string } })?.data?.message || t('errors.network'))
                  : t('errors.network')}
              </p>
              <Link
                to="/dashboard"
                className="inline-block px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
              >
                {t('profile.error.back')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const user = data.data.user;

  return (
    <div className="min-h-screen relative">
      <div className="container mx-auto px-3 sm:px-4 py-8 sm:py-16 content-overlay">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8 text-center animate-slide-up">
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-4">
              {t('profile.title')}
            </h1>
            <p className="text-neutral-medium text-lg font-body">
              {t('profile.subtitle')}
            </p>
          </div>

          {/* Profile Card */}
          <div className="card-glass rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 animate-scale-in">
            {!isEditing ? (
              <>
                {/* View Mode */}
                <div className="space-y-6">
                  {/* User Info Section */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-neutral-medium mb-1 font-body">
                        {t('profile.info.firstName')}
                      </label>
                      <p className="text-lg text-neutral-light font-body">
                        {user.firstName}
                      </p>
                    </div>
                    {user.lastName && (
                      <div>
                        <label className="block text-sm font-medium text-neutral-medium mb-1 font-body">
                          {t('profile.info.lastName')}
                        </label>
                        <p className="text-lg text-neutral-light font-body">
                          {user.lastName}
                        </p>
                      </div>
                    )}
                    {user.email && (
                      <div>
                        <label className="block text-sm font-medium text-neutral-medium mb-1 font-body">
                          {t('profile.info.email')}
                        </label>
                        <p className="text-lg text-neutral-light font-body">
                          {user.email}
                        </p>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-neutral-medium mb-1 font-body">
                        {t('profile.info.language')}
                      </label>
                      <p className="text-lg text-neutral-light font-body">
                        {user.language === 'ar' ? t('profile.edit.languageAr') :
                         user.language === 'en' ? t('profile.edit.languageEn') :
                         user.language === 'yo' ? t('profile.edit.languageYo') :
                         user.language === 'ha' ? t('profile.edit.languageHa') :
                         user.language === 'hi' ? t('profile.edit.languageHi') :
                         user.language === 'es' ? t('profile.edit.languageEs') :
                         user.language === 'bn' ? t('profile.edit.languageBn') :
                         user.language || t('profile.edit.languageEn')}
                      </p>
                    </div>
                    {user.telegramId && (
                      <div>
                        <label className="block text-sm font-medium text-neutral-medium mb-1 font-body">
                          {t('profile.info.telegramId')}
                        </label>
                        <p className="text-lg text-neutral-light font-body">
                          {user.telegramId}
                        </p>
                      </div>
                    )}
                    {user.username && (
                      <div>
                        <label className="block text-sm font-medium text-neutral-medium mb-1 font-body">
                          {t('profile.info.username')}
                        </label>
                        <p className="text-lg text-neutral-light font-body">
                          @{user.username}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Balance Section */}
                  <div className="border-t border-neutral-dark pt-6">
                    <h3 className="text-xl font-heading font-semibold text-brand-accent mb-4">
                      {t('profile.balance.title')}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-neutral-dark/30 rounded-lg p-4">
                        <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                          {t('profile.balance.main')}
                        </label>
                        <p className="text-2xl font-bold text-brand-accent font-heading">
                          {user.balance.toFixed(2)} USD
                        </p>
                      </div>
                      <div className="bg-neutral-dark/30 rounded-lg p-4">
                        <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                          {t('profile.balance.referral')}
                        </label>
                        <p className="text-2xl font-bold text-brand-accent font-heading">
                          {user.referralBalance.toFixed(2)} USD
                        </p>
                      </div>
                      <div className="bg-neutral-dark/30 rounded-lg p-4">
                        <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                          {t('profile.balance.cashback')}
                        </label>
                        <p className="text-2xl font-bold text-brand-accent font-heading">
                          {user.cashbackBalance.toFixed(2)} USD
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Statistics Section */}
                  <div className="border-t border-neutral-dark pt-6">
                    <h3 className="text-xl font-heading font-semibold text-brand-accent mb-4">
                      {t('profile.stats.title')}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {typeof user.referralCount === 'number' && (
                        <div className="bg-neutral-dark/30 rounded-lg p-4">
                          <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                            {t('profile.stats.referrals')}
                          </label>
                          <p className="text-2xl font-bold text-brand-accent font-heading">
                            {user.referralCount}
                          </p>
                        </div>
                      )}
                      {typeof user.apiKeysCount === 'number' && (
                        <div className="bg-neutral-dark/30 rounded-lg p-4">
                          <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                            {t('profile.stats.apiKeys')}
                          </label>
                          <p className="text-2xl font-bold text-brand-accent font-heading">
                            {user.apiKeysCount}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="border-t border-neutral-dark pt-6 flex flex-col sm:flex-row gap-3 sm:gap-4">
                    <button
                      onClick={() => setIsEditing(true)}
                      className="flex-1 px-4 sm:px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300 text-sm sm:text-base"
                    >
                      {t('profile.actions.edit')}
                    </button>
                    <Link
                      to="/balance"
                      className="flex-1 px-4 sm:px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300 text-center text-sm sm:text-base"
                    >
                      {t('profile.actions.recharge')}
                    </Link>
                    <Link
                      to="/api-keys"
                      className="flex-1 px-4 sm:px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300 text-center text-sm sm:text-base"
                    >
                      {t('profile.actions.apiKeys')}
                    </Link>
                    <Link
                      to="/dashboard"
                      className="flex-1 px-4 sm:px-6 py-3 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transform hover:scale-105 transition-all duration-300 text-center text-sm sm:text-base"
                    >
                      {t('profile.actions.back')}
                    </Link>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Edit Mode */}
                <div className="space-y-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-heading font-bold text-brand-accent">
                      {t('profile.edit.title')}
                    </h2>
                    <button
                      onClick={() => setIsEditing(false)}
                      className="text-neutral-medium hover:text-neutral-light transition-colors font-body"
                    >
                      {t('common.close')}
                    </button>
                  </div>
                  <ProfileEditForm user={user} onSuccess={handleEditSuccess} />
                </div>
              </>
            )}
          </div>

          {/* Webhook Settings Section */}
          <div className="mt-6">
            <WebhookSettings />
          </div>

          {/* Email 2FA Section */}
          <div className="mt-6">
            <div className="card-glass rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 animate-scale-in">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <div>
                  <h3 className="text-xl font-heading font-semibold text-brand-accent">
                    {t('profile.2fa.title')}
                  </h3>
                  <p className="text-sm text-neutral-medium font-body mt-1">
                    {t('profile.2fa.description')}
                  </p>
                </div>
                {!show2faOtp && (
                  <button
                    onClick={handleToggle2faClick}
                    disabled={isToggling2fa}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2 disabled:opacity-50 ${
                      user.email2faEnabled ? 'bg-brand-accent' : 'bg-neutral-dark'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                        user.email2faEnabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                )}
              </div>

              {/* Success/Error Messages */}
              {otpSuccess && (
                <div className="p-4 bg-success/20 border border-success rounded-lg animate-slide-down mb-4">
                  <p className="text-green-300 text-sm font-body">{otpSuccess}</p>
                </div>
              )}

              {otpError && !show2faOtp && (
                <div className="p-4 bg-danger/20 border border-danger rounded-lg animate-slide-down mb-4">
                  <p className="text-red-300 text-sm font-body">{otpError}</p>
                </div>
              )}

              {/* OTP Verification Form */}
              {show2faOtp && (
                <form onSubmit={(e) => { void handleVerify2faOtp(e); }} className="space-y-4">
                  <p className="text-sm text-neutral-medium font-body">
                    {otpAction === 'enable'
                      ? t('profile.2fa.otpSentEnable', { email: otpEmail })
                      : t('profile.2fa.otpSentDisable')}
                  </p>

                  {otpError && (
                    <div className="p-3 bg-danger/20 border border-danger rounded-lg">
                      <p className="text-red-300 text-sm font-body">{otpError}</p>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={otp}
                      onChange={(e) => {
                        setOtp(e.target.value.replace(/[^0-9]/g, ''));
                        setOtpError(null);
                      }}
                      className="w-full sm:flex-1 px-4 py-3 rounded-xl border bg-brand-primary/50 text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent transition-all duration-300 font-body text-center tracking-widest border-neutral-dark focus:border-brand-accent"
                      placeholder="000000"
                      maxLength={6}
                      autoFocus
                    />
                    <button
                      type="submit"
                      disabled={isToggling2fa || isConfirming2fa}
                      className="w-full sm:w-auto px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-xl hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {isToggling2fa || isConfirming2fa ? (
                        <>
                          <ButtonSpinner />
                          <span>{t('common.loading')}</span>
                        </>
                      ) : (
                        t('common.verify')
                      )}
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setShow2faOtp(false);
                      setOtp('');
                      setOtpError(null);
                    }}
                    className="text-sm text-neutral-medium hover:text-neutral-light transition-colors font-body"
                  >
                    {t('auth.login.backToLogin')}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

